import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { DocStatusBadge } from "@/components/StatusBadge";
import { DisclosureClassBadge, DivisionBadge, ExpiryBadge } from "@/components/VaultBadges";
import { toast } from "sonner";
import { Vault, Upload, ExternalLink } from "lucide-react";

// Kandake Document Repository — embedded panel for Track/Title records.
// Shows every vault document linked to this track (server-side division
// enforcement applies) with the upload control directly adjoining the
// request/status per Spec §7.

export function VaultDocumentsPanel({ trackId }) {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [canWriteVault, setCanWriteVault] = useState(false);
  const [loading, setLoading] = useState(true);

  async function load() {
    if (!trackId) return;
    setLoading(true);
    try {
      const [cfg, r] = await Promise.all([
        api.get("/vault/config"),
        api.get("/vault/documents", { params: { record_type: "track", record_id: trackId } }),
      ]);
      setCanWriteVault(!!cfg.data.you_can_write);
      setDocs(r.data.documents || []);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [trackId]);

  async function uploadFor(doc, file) {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/vault/documents/${doc.id}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${doc.document_type} uploaded`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  }

  return (
    <Card className="mt-4" data-testid="vault-track-panel">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display text-xl flex items-center gap-2">
            <Vault className="w-5 h-5" style={{ color: "hsl(40 48% 32%)" }} />
            Document Vault
          </div>
          <Button size="sm" variant="outline" onClick={() => navigate("/vault")} data-testid="vault-track-panel-open-vault">
            <ExternalLink className="w-3.5 h-3.5 mr-1.5" />Open Vault
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Repository documents linked to this title. One upload may serve multiple records — manage links, versions and furnishing in the Vault.
        </p>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type / Title</TableHead>
              <TableHead>Class</TableHead>
              <TableHead>Division</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Expiry</TableHead>
              <TableHead className="w-[170px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground text-sm">Loading vault documents…</TableCell></TableRow>
            )}
            {!loading && docs.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground text-sm" data-testid="vault-track-panel-empty">
                No vault documents linked to this title yet.
              </TableCell></TableRow>
            )}
            {docs.map(d => (
              <TableRow key={d.id} data-testid={`vault-track-panel-row-${d.id}`}>
                <TableCell className="max-w-[220px]">
                  <div className="font-medium text-sm truncate">{d.document_type}</div>
                  {d.title && <div className="text-xs text-muted-foreground truncate">{d.title}</div>}
                </TableCell>
                <TableCell><DisclosureClassBadge value={d.disclosure_class} /></TableCell>
                <TableCell><DivisionBadge value={d.division_scope} /></TableCell>
                <TableCell><DocStatusBadge status={d.document_status} /></TableCell>
                <TableCell><ExpiryBadge isExpired={d.is_expired} expirationDate={d.expiration_date} /></TableCell>
                <TableCell>
                  {/* Upload control directly adjoining the request/status (Spec §7) */}
                  {canWriteVault && !d.file_storage_path ? (
                    <label className="inline-flex">
                      <input type="file" className="hidden" onChange={(e) => uploadFor(d, e.target.files?.[0])} data-testid={`vault-track-panel-upload-${d.id}`} />
                      <Button size="sm" variant="outline" asChild>
                        <span className="cursor-pointer"><Upload className="w-3.5 h-3.5 mr-1.5" />Upload</span>
                      </Button>
                    </label>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => navigate(`/vault?doc=${d.id}`)} data-testid={`vault-track-panel-view-${d.id}`}>
                      View in Vault
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
